import { useSubmitEnrollmentJointConfirmInformationData } from '@egw/api'
import { EnrollmentForm, ModalDrawer, type EnrollmentFormField, type FieldValues } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { type FC, useCallback, useMemo, useState, useEffect } from 'react'
import type { EditFirstNameModalProps, FirstNameFormData } from './edit-first-name-modal.types'

const log = new Logger('Enrollment - Joint - EditFirstNameModal')

export const EditFirstNameModal: FC<EditFirstNameModalProps> = ({
  isOpen,
  onClose,
  currentValue,
  currentLastName,
  onSuccess,
}) => {
  const { t } = useTranslation('features-enrollment')

  const { mutate, isLoading: isSubmitting } = useSubmitEnrollmentJointConfirmInformationData()
  const [submitError, setSubmitError] = useState<string | undefined>()

  // Local state to track the updated value to prevent brief revert
  const [localFirstName, setLocalFirstName] = useState<string>(currentValue ?? '')

  // Update local state when currentValue changes (e.g., when modal reopens)
  useEffect(() => {
    setLocalFirstName(currentValue ?? '')
  }, [currentValue])

  // Define form fields
  const fields: EnrollmentFormField[][] = useMemo(
    () => [
      [
        {
          name: 'firstName',
          type: 'text',
          label: t('joint.confirm_information.edit_modals.first_name_label'),
          placeholder: t('joint.confirm_information.edit_modals.first_name_placeholder'),
          required: true,
          isPII: true,
          testId: 'edit-firstName-input',
          validationRules: {
            pattern: {
              value: /^[A-Za-z.' -]{2,30}$/,
              message: t('joint.confirm_information.edit_modals.first_name_invalid'),
            },
          },
        },
      ],
    ],
    [t],
  )

  // Handle form submission
  const handleSubmit = useCallback(
    async (formData: FieldValues): Promise<void> => {
      // Clear any previous errors
      setSubmitError(undefined)

      // Cast to our specific form data type
      const data = formData as FirstNameFormData

      // Update local state immediately to prevent revert during submission
      setLocalFirstName(data.firstName)

      try {
        log.info('Submitting first name update')

        const response = await mutate({
          activePerson: {
            name: {
              firstName: data.firstName,
              lastName: currentLastName ?? '', // Keep existing last name
            },
          },
        })

        // Check for API errors
        if (response?.isError) {
          log.error('Error updating first name', response.errors)
          setSubmitError(t('common.generic_error'))

          // Revert local state on API error
          setLocalFirstName(currentValue ?? '')
          return
        }

        log.info('First name updated successfully')

        // Call success callback with the updated first name
        if (onSuccess) {
          await Promise.resolve(onSuccess(data.firstName))
        }

        // Close modal
        onClose()
      } catch (error) {
        log.error('Error submitting first name update', error)
        setSubmitError(t('common.generic_error'))

        // Revert local state on error
        setLocalFirstName(currentValue ?? '')
      }
    },
    [mutate, currentLastName, onSuccess, onClose, t, currentValue],
  )

  return (
    <ModalDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('joint.confirm_information.edit_modals.edit_first_name_title')}
      testId="edit-firstName-modal"
    >
      <EnrollmentForm
        fields={fields}
        defaultValues={{ firstName: localFirstName }}
        onSubmit={handleSubmit}
        submitButtonText={t('common.save')}
        isSubmitting={isSubmitting}
        submitError={submitError}
        mode="onChange"
        testId="edit-firstName-form"
      />
    </ModalDrawer>
  )
}

export type { EditFirstNameModalProps }
